import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  EtablissementEntrepotLink,
  EtablissementRow,
  LogicielCaisseParametreAssociationsService,
} from './logiciel-caisse-parametre-associations.service';
import {
  EntrepotTableRow,
  LogicielCaisseEntrepotService,
} from './logiciel-caisse-entrepot.service';
import {
  CaisseTableRow,
  LogicielCaisseCaisseService,
} from './logiciel-caisse-caisse.service';
import {
  CaisseVendeurLink,
  LogicielCaisseVendeurService,
} from './logiciel-caisse-vendeur.service';
import {
  LogicielCaisseTicketTemplateService,
  TicketTemplateRow,
} from './logiciel-caisse-ticket-template.service';

export type LcSetupCheckKey =
  | 'etablissement'
  | 'entrepot'
  | 'caisse'
  | 'vendeur'
  | 'ticket';

export interface LcSetupCheckItem {
  key: LcSetupCheckKey;
  label: string;
  description: string;
  done: boolean;
  count: number;
  route: string;
}

export interface LcSetupTreeNode {
  type: 'etablissement' | 'entrepot' | 'caisse' | 'vendeur';
  id: number | string;
  label: string;
  meta?: string;
  children: LcSetupTreeNode[];
}

export interface LcSetupOverview {
  loading: boolean;
  isEmpty: boolean;
  coreComplete: boolean;
  completedSteps: number;
  totalSteps: number;
  progressPercent: number;
  checks: LcSetupCheckItem[];
  tree: LcSetupTreeNode[];
}

export interface LcSetupWizardContext {
  etablissementId: number | null;
  etablissementLibelle: string;
  entrepotId: number | null;
  entrepotLibelle: string;
  caisseId: number | null;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseSetupService {
  private wizardContext: LcSetupWizardContext = this.emptyWizardContext();

  constructor(
    private readonly associationsService: LogicielCaisseParametreAssociationsService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly caisseService: LogicielCaisseCaisseService,
    private readonly vendeurService: LogicielCaisseVendeurService,
    private readonly ticketService: LogicielCaisseTicketTemplateService
  ) {}

  resetWizardContext(): void {
    this.wizardContext = this.emptyWizardContext();
  }

  getWizardContext(): LcSetupWizardContext {
    return { ...this.wizardContext };
  }

  patchWizardContext(partial: Partial<LcSetupWizardContext>): void {
    this.wizardContext = { ...this.wizardContext, ...partial };
  }

  loadOverview(): Observable<LcSetupOverview> {
    return forkJoin({
      etablissements: this.associationsService.listEtablissements().pipe(catchError(() => of([] as EtablissementRow[]))),
      etabLinks: this.associationsService.listEtablissementEntrepotLinks().pipe(catchError(() => of([] as EtablissementEntrepotLink[]))),
      entrepots: this.entrepotService.getEntrepotsErpTableRows().pipe(catchError(() => of([] as EntrepotTableRow[]))),
      caisses: this.caisseService.getCaissesTableRows().pipe(catchError(() => of([] as CaisseTableRow[]))),
      vendeurLinks: this.vendeurService.listCaisseVendeurLinks().pipe(catchError(() => of([] as CaisseVendeurLink[]))),
      tickets: this.ticketService.listForStation().pipe(catchError(() => of([] as TicketTemplateRow[]))),
    }).pipe(
      map(({ etablissements, etabLinks, entrepots, caisses, vendeurLinks, tickets }) =>
        this.buildOverview(etablissements, etabLinks, entrepots, caisses, vendeurLinks, tickets)
      )
    );
  }

  defaultEtablissementName(): string {
    const fromReg = localStorage.getItem('registration_nom_entreprise');
    if (fromReg?.trim()) {
      return fromReg.trim();
    }
    try {
      const raw = localStorage.getItem('currentUser');
      if (raw) {
        const user = JSON.parse(raw) as { stations?: { label?: string }[] };
        const label = user?.stations?.[0]?.label;
        if (label?.trim()) {
          return label.trim();
        }
      }
    } catch {
      /* ignore */
    }
    return 'Mon établissement';
  }

  suggestEtablissementCode(existing: EtablissementRow[]): string {
    const used = new Set(existing.map((e) => e.code_etablissement?.trim()).filter(Boolean));
    for (let i = 1; i <= 99; i++) {
      const code = String(i).padStart(3, '0');
      if (!used.has(code)) {
        return code;
      }
    }
    return '001';
  }

  private emptyWizardContext(): LcSetupWizardContext {
    return {
      etablissementId: null,
      etablissementLibelle: '',
      entrepotId: null,
      entrepotLibelle: '',
      caisseId: null,
    };
  }

  private buildOverview(
    etablissements: EtablissementRow[],
    etabLinks: EtablissementEntrepotLink[],
    entrepots: EntrepotTableRow[],
    caisses: CaisseTableRow[],
    vendeurLinks: CaisseVendeurLink[],
    tickets: TicketTemplateRow[]
  ): LcSetupOverview {
    const entrepotById = new Map<number, EntrepotTableRow>();
    for (const e of entrepots) {
      const id = Number(e.id);
      if (Number.isFinite(id)) {
        entrepotById.set(id, e);
      }
    }

    const caissesByEntrepot = new Map<number, CaisseTableRow[]>();
    for (const c of caisses) {
      const eid = Number(c.entrepot_id);
      if (!Number.isFinite(eid)) {
        continue;
      }
      const list = caissesByEntrepot.get(eid) ?? [];
      list.push(c);
      caissesByEntrepot.set(eid, list);
    }

    const vendeursByCaisse = new Map<number, CaisseVendeurLink[]>();
    for (const link of vendeurLinks) {
      const list = vendeursByCaisse.get(link.caisse_id) ?? [];
      list.push(link);
      vendeursByCaisse.set(link.caisse_id, list);
    }

    const entrepotIdsLinked = new Set(etabLinks.map((l) => l.entrepot_id));
    const tree: LcSetupTreeNode[] = [];

    for (const etab of etablissements) {
      const linkedEntrepotIds = etabLinks
        .filter((l) => l.etablissement_id === etab.id)
        .map((l) => l.entrepot_id);

      const entrepotNodes: LcSetupTreeNode[] = linkedEntrepotIds.map((entrepotId) => {
        const ent = entrepotById.get(entrepotId);
        const label = ent?.LIBELLÉ?.trim() || ent?.['CODE ENTREPÔT']?.trim() || `Entrepôt ${entrepotId}`;
        const caisseRows = caissesByEntrepot.get(entrepotId) ?? [];
        const caisseNodes: LcSetupTreeNode[] = caisseRows.map((c) => {
          const cid = Number(c.id);
          const vendeurRows = vendeursByCaisse.get(cid) ?? [];
          return {
            type: 'caisse' as const,
            id: c.id,
            label: c.CAISSE || `Caisse ${c.numero_de_caisse ?? c.id}`,
            meta: c.STATUT,
            children: vendeurRows.map((v) => ({
              type: 'vendeur' as const,
              id: v.vendeur_id,
              label: `${v.vendeur_prenom ?? ''} ${v.vendeur_nom ?? ''}`.trim() || v.vendeur_identifiant || 'Vendeur',
              meta: v.vendeur_identifiant,
              children: [],
            })),
          };
        });
        return {
          type: 'entrepot' as const,
          id: entrepotId,
          label,
          meta: ent?.['CODE ENTREPÔT'],
          children: caisseNodes,
        };
      });

      tree.push({
        type: 'etablissement',
        id: etab.id,
        label: etab.libelle,
        meta: etab.code_etablissement,
        children: entrepotNodes,
      });
    }

    const hasEtablissement = etablissements.length > 0;
    const hasEntrepot = entrepots.length > 0 && entrepotIdsLinked.size > 0;
    const hasCaisse = caisses.length > 0;
    const hasVendeur = vendeurLinks.length > 0;
    const hasTicket = tickets.some((t) => !t.is_default || t.updated_at);

    const checks: LcSetupCheckItem[] = [
      {
        key: 'etablissement',
        label: 'Établissement',
        description: 'Votre magasin ou succursale',
        done: hasEtablissement,
        count: etablissements.length,
        route: '/main/parametre/etablissements',
      },
      {
        key: 'entrepot',
        label: 'Entrepôt lié',
        description: 'Stock rattaché à l’établissement',
        done: hasEntrepot,
        count: entrepotIdsLinked.size,
        route: '/main/parametre/entrepots',
      },
      {
        key: 'caisse',
        label: 'Caisse',
        description: 'Poste de vente opérationnel',
        done: hasCaisse,
        count: caisses.length,
        route: '/main/parametre/caisses',
      },
      {
        key: 'vendeur',
        label: 'Vendeur',
        description: 'Utilisateur affecté à une caisse',
        done: hasVendeur,
        count: vendeurLinks.length,
        route: '/main/parametre/vendeur-caisse',
      },
      {
        key: 'ticket',
        label: 'Template ticket',
        description: 'Mise en page des tickets de caisse',
        done: hasTicket,
        count: tickets.length,
        route: '/main/parametre/ticket-templates',
      },
    ];

    const completedSteps = checks.filter((c) => c.done).length;
    const totalSteps = checks.length;
    const coreComplete = hasEtablissement && hasEntrepot && hasCaisse;
    const isEmpty = !coreComplete;

    return {
      loading: false,
      isEmpty,
      coreComplete,
      completedSteps,
      totalSteps,
      progressPercent: Math.round((completedSteps / totalSteps) * 100),
      checks,
      tree,
    };
  }
}
